import {
  Children,
  cloneElement,
  createRef,
  forwardRef,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import gsap from 'gsap';
import './CardSwap.css';

export const Card = forwardRef(({ customClass, className = '', ...rest }, ref) => (
  <div ref={ref} {...rest} className={`card ${customClass ?? ''} ${className}`.trim()} />
));
Card.displayName = 'Card';

const makeSlot = (i, distX, distY, total) => ({
  x: i * distX,
  y: -i * distY,
  z: -i * distX * 1.5,
  zIndex: total - i,
});

const placeNow = (el, slot, skew) => {
  gsap.set(el, {
    x: slot.x,
    y: slot.y,
    z: slot.z,
    xPercent: -50,
    yPercent: -50,
    skewY: skew,
    transformOrigin: 'center center',
    zIndex: slot.zIndex,
    force3D: true,
  });
};

function CardSwap({
  width = 500,
  height = 400,
  cardDistance = 60,
  verticalDistance = 70,
  delay = 5000,
  pauseOnHover = false,
  freezeOnCardHover = true,
  hoverLift = 22,
  onCardClick,
  skewAmount = 6,
  easing = 'elastic',
  children,
}) {
  const childArr = useMemo(() => Children.toArray(children), [children]);
  const refs = useMemo(() => childArr.map(() => createRef()), [childArr.length]);

  const order = useRef(Array.from({ length: childArr.length }, (_, i) => i));
  const tlRef = useRef(null);
  const intervalRef = useRef(null);
  const container = useRef(null);
  const stackAreaRef = useRef(null);
  const hoveredCardRef = useRef(null);
  const bringToFrontRef = useRef(null);

  useEffect(() => {
    const animConfig =
      easing === 'elastic'
        ? {
            ease: 'elastic.out(0.6,0.9)',
            durDrop: 2,
            durMove: 2,
            durReturn: 2,
            promoteOverlap: 0.9,
            returnDelay: 0.05,
          }
        : {
            ease: 'power1.inOut',
            durDrop: 0.8,
            durMove: 0.8,
            durReturn: 0.8,
            promoteOverlap: 0.45,
            returnDelay: 0.2,
          };

    const total = refs.length;
    if (total === 0) return undefined;

    const applySlot = (el, slotIdx, lift = 0, bringFront = false) => {
      const slot = makeSlot(slotIdx, cardDistance, verticalDistance, refs.length);
      gsap.to(el, {
        x: slot.x,
        y: slot.y - lift,
        z: slot.z,
        zIndex: bringFront ? refs.length + 5 : slot.zIndex,
        skewY: skewAmount,
        duration: 0.35,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    };

    const layoutAllCards = (liftHovered = true) => {
      order.current.forEach((cardIdx, slotIdx) => {
        const el = refs[cardIdx].current;
        if (!el) return;
        const isHovered = liftHovered && hoveredCardRef.current === cardIdx;
        applySlot(el, slotIdx, isHovered ? hoverLift : 0, isHovered);
      });
    };

    refs.forEach((r, i) => {
      if (r.current) {
        placeNow(r.current, makeSlot(i, cardDistance, verticalDistance, total), skewAmount);
      }
    });

    const pauseStack = () => {
      tlRef.current?.pause();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const resumeStack = () => {
      if (hoveredCardRef.current !== null) return;
      tlRef.current?.play();
      if (!intervalRef.current) {
        intervalRef.current = window.setInterval(swap, delay);
      }
    };

    const swap = () => {
      if (order.current.length < 2 || hoveredCardRef.current !== null) return;

      const [front, ...rest] = order.current;
      const elFront = refs[front].current;
      if (!elFront) return;

      const tl = gsap.timeline();
      tlRef.current = tl;

      tl.to(elFront, {
        y: '+=500',
        duration: animConfig.durDrop,
        ease: animConfig.ease,
      });

      tl.addLabel('promote', `-=${animConfig.durDrop * animConfig.promoteOverlap}`);
      rest.forEach((idx, i) => {
        const el = refs[idx].current;
        if (!el) return;
        const slot = makeSlot(i, cardDistance, verticalDistance, refs.length);
        tl.set(el, { zIndex: slot.zIndex }, 'promote');
        tl.to(
          el,
          {
            x: slot.x,
            y: slot.y,
            z: slot.z,
            duration: animConfig.durMove,
            ease: animConfig.ease,
          },
          `promote+=${i * 0.15}`
        );
      });

      const backSlot = makeSlot(refs.length - 1, cardDistance, verticalDistance, refs.length);
      tl.addLabel('return', `promote+=${animConfig.durMove * animConfig.returnDelay}`);
      tl.call(
        () => {
          gsap.set(elFront, { zIndex: backSlot.zIndex });
        },
        undefined,
        'return'
      );
      tl.to(
        elFront,
        {
          x: backSlot.x,
          y: backSlot.y,
          z: backSlot.z,
          duration: animConfig.durReturn,
          ease: animConfig.ease,
        },
        'return'
      );

      tl.call(() => {
        order.current = [...rest, front];
      });
    };

    bringToFrontRef.current = (clickedIdx) => {
      const current = order.current;
      if (current[0] === clickedIdx) return;

      tlRef.current?.kill();
      pauseStack();

      const oldFront = current[0];
      const others = current.filter((idx) => idx !== clickedIdx && idx !== oldFront);
      order.current = [clickedIdx, ...others, oldFront];

      layoutAllCards(true);

      if (hoveredCardRef.current === null) {
        window.setTimeout(() => resumeStack(), 380);
      }
    };

    swap();
    intervalRef.current = window.setInterval(swap, delay);

    const cardCleanups = [];

    if (freezeOnCardHover) {
      refs.forEach((r, i) => {
        const el = r.current;
        if (!el) return;

        const onEnter = () => {
          hoveredCardRef.current = i;
          pauseStack();
          const slotIdx = order.current.indexOf(i);
          if (slotIdx >= 0) {
            applySlot(el, slotIdx, hoverLift, true);
          }
        };

        const onLeave = () => {
          if (hoveredCardRef.current !== i) return;
          hoveredCardRef.current = null;
          const slotIdx = order.current.indexOf(i);
          if (slotIdx >= 0) {
            applySlot(el, slotIdx, 0, false);
          }
        };

        el.addEventListener('mouseenter', onEnter);
        el.addEventListener('mouseleave', onLeave);
        cardCleanups.push(() => {
          el.removeEventListener('mouseenter', onEnter);
          el.removeEventListener('mouseleave', onLeave);
        });
      });
    }

    const stackArea = stackAreaRef.current;
    const onStackLeave = () => {
      hoveredCardRef.current = null;
      layoutAllCards(false);
      resumeStack();
    };

    if (stackArea) {
      stackArea.addEventListener('mouseleave', onStackLeave);
    }

    const node = container.current;
    let pause;
    let resume;

    if (pauseOnHover && node) {
      pause = () => {
        tlRef.current?.pause();
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
      resume = () => {
        tlRef.current?.play();
        intervalRef.current = window.setInterval(swap, delay);
      };
      node.addEventListener('mouseenter', pause);
      node.addEventListener('mouseleave', resume);
    }

    return () => {
      bringToFrontRef.current = null;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      cardCleanups.forEach((fn) => fn());
      if (stackArea) {
        stackArea.removeEventListener('mouseleave', onStackLeave);
      }
      hoveredCardRef.current = null;
      if (node && pause && resume) {
        node.removeEventListener('mouseenter', pause);
        node.removeEventListener('mouseleave', resume);
      }
      tlRef.current?.kill();
    };
  }, [cardDistance, verticalDistance, delay, pauseOnHover, freezeOnCardHover, hoverLift, skewAmount, easing, refs]);

  const rendered = childArr.map((child, i) =>
    isValidElement(child)
      ? cloneElement(child, {
          key: child.key ?? i,
          ref: refs[i],
          style: { width, height, ...(child.props.style ?? {}) },
          onClick: (e) => {
            child.props.onClick?.(e);
            bringToFrontRef.current?.(i);
            onCardClick?.(i);
          },
        })
      : child
  );

  return (
    <div ref={stackAreaRef} className="card-swap-stack-area">
      <div ref={container} className="card-swap-container" style={{ width, height }}>
        {rendered}
      </div>
    </div>
  );
}

export default CardSwap;
